import { Map } from 'immutable';
import { createReducer, SimpleActionCreator } from 'redux-act';

import { accountReactivated } from '@/store/actions/authentication/reactivateAccount';
import { companyGuestsFetched, companyGuestsFetchFailed, fetchCompanyGuests } from '@/store/actions/company/fetchCompanyGuests';
import { subscriptionCreated } from '@/store/actions/subscription/createSubscription';
import { passwordChanged } from '@/store/actions/user/changePassword';

import { fetchPlace, placeFetched, placeFetchFailed } from '../../store/actions/place/fetchPlace';
import FetchState, { completeFetch, dontFetch, failFetch, requireFetch, startFetch } from '../../utils/FetchState';
import { getPriceKey, getSubscriptionKey } from '../../utils/subscriptions';
import { authenticationRequired } from '../actions/authentication/authenticationRequired';
import { fetchLoggedInUser, loggedInUserFetched, loggedInUserFetchFailed } from '../actions/authentication/fetchLoggedInUser';
import { login, loginFailed, loginSuccessful } from '../actions/authentication/login';
import { logout } from '../actions/authentication/logout';
import { fetchLayouts, layoutsFetched, layoutsFetchFailed } from '../actions/layout/fetchLayouts';
import { layoutsUpdated } from '../actions/layout/layoutsUpdated';
import { fetchNotifications, notificationsFetched, notificationsFetchFailed } from '../actions/notification/fetchNotifications';
import { placeAdded } from '../actions/place/addPlace';
import { countriesFetched, countriesFetchFailed, fetchCountries } from '../actions/place/fetchCountries';
import { fetchPlaceLayouts, placeLayoutsFetched, placeLayoutsFetchFailed } from '../actions/place/fetchPlaceLayouts';
import { activatePlaceFetched, activatePlaceFetchFailed, fetchActivatePlace } from '../actions/registration/activatePlace';
import { employeeInvitationFetched, employeeInvitationFetchFailed, fetchEmployeeInvitation } from '../actions/registration/fetchEmployeeInvitation';
import { fetchReservations, reservationsFetched, reservationsFetchFailed } from '../actions/reservation/fetchReservations';
import { reservationsUpdated } from '../actions/reservation/reservationsUpdated';
import { employeesFetched, employeesFetchFailed, fetchEmployees } from '../actions/setting/company/fetchEmployees';
import { contactUpdated } from '../actions/setting/contactUpdated';
import { facebookPageUpdated } from '../actions/setting/facebook/facebookPageUpdated';
import { facebookPageFetched, facebookPageFetchFailed, fetchFacebookPage } from '../actions/setting/facebook/fetchFacebookPage';
import { facebookPagesFetched, facebookPagesFetchFailed, fetchFacebookPages } from '../actions/setting/facebook/fetchFacebookPages';
import { contactFetched, contactFetchFailed, fetchContact } from '../actions/setting/fetchContact';
import { fetchHours, hoursFetched, hoursFetchFailed } from '../actions/setting/fetchHours';
import { fetchPlaceLocalization, placeLocalizationFetched, placeLocalizationFetchFailed } from '../actions/setting/fetchPlaceLocalization';
import { fetchProfile, profileFetched, profileFetchFailed } from '../actions/setting/fetchProfile';
import { fetchReservationSetting, reservationSettingFetched, reservationSettingFetchFailed } from '../actions/setting/fetchReservationSetting';
import { hoursUpdated } from '../actions/setting/hoursUpdated';
import { profileUpdated } from '../actions/setting/profileUpdated';
import { reservationSettingUpdated } from '../actions/setting/reservationSettingUpdated';
import { fetchPrices, pricesFetched, pricesFetchFailed } from '../actions/subscription/fetchPrices';
import { fetchSubscriptions, subscriptionsFetched, subscriptionsFetchFailed } from '../actions/subscription/fetchSubscriptions';

interface PlaceFetchStateState {
  loggedInUserFetchState: FetchState;
  placeLocalizationFetchState: Map<string, FetchState>;
  notificationFetchState: FetchState;
  layoutFetchStates: Map<string, FetchState>;
  reservationFetchState: Map<string, FetchState>;
  contactFetchState: Map<string, FetchState>;
  hoursFetchState: Map<string, FetchState>;
  profileFetchState: Map<string, FetchState>;
  reservationSettingFetchState: Map<string, FetchState>;
  placeLayouts: Map<string, FetchState>;
  companyGuests: FetchState;
  places: Map<string, FetchState>;
  facebookPages: Map<string, FetchState>;
  facebookPage: Map<string, FetchState>;
  employeeFetchState: FetchState;
  prices: Map<string, FetchState>;
  subscriptions: Map<string, FetchState>;
}

export interface FetchStateType extends PlaceFetchStateState {
  authenticationTokenFetchState: FetchState;
  activatePlaceFetchState: FetchState;
  countriesFetchState: FetchState;
  tokenFetchStates: Map<string, FetchState>;
}

function getAfterLoginPlaceFetchStateState(): PlaceFetchStateState {
  return {
    loggedInUserFetchState: requireFetch(),
    placeLocalizationFetchState: Map<string, FetchState>(),
    notificationFetchState: requireFetch(),
    layoutFetchStates: Map<string, FetchState>(),
    reservationFetchState: Map<string, FetchState>(),
    contactFetchState: Map<string, FetchState>(),
    hoursFetchState: Map<string, FetchState>(),
    profileFetchState: Map<string, FetchState>(),
    reservationSettingFetchState: Map<string, FetchState>(),
    placeLayouts: Map<string, FetchState>(),
    companyGuests: requireFetch(),
    places: Map<string, FetchState>(),
    facebookPages: Map<string, FetchState>(),
    facebookPage: Map<string, FetchState>(),
    prices: Map<string, FetchState>(),
    subscriptions: Map<string, FetchState>(),
    employeeFetchState: requireFetch(),
  };
}

function getAfterLogoutPlaceFetchStateState(): PlaceFetchStateState {
  return {
    loggedInUserFetchState: dontFetch(),
    placeLocalizationFetchState: Map<string, FetchState>(),
    notificationFetchState: dontFetch(),
    layoutFetchStates: Map<string, FetchState>(),
    reservationFetchState: Map<string, FetchState>(),
    contactFetchState: Map<string, FetchState>(),
    hoursFetchState: Map<string, FetchState>(),
    profileFetchState: Map<string, FetchState>(),
    reservationSettingFetchState: Map<string, FetchState>(),
    placeLayouts: Map<string, FetchState>(),
    companyGuests: dontFetch(),
    places: Map<string, FetchState>(),
    facebookPages: Map<string, FetchState>(),
    facebookPage: Map<string, FetchState>(),
    prices: Map<string, FetchState>(),
    subscriptions: Map<string, FetchState>(),
    employeeFetchState: dontFetch(),
  };
}

const defaultState: FetchStateType = {
  authenticationTokenFetchState: dontFetch(),
  activatePlaceFetchState: requireFetch(),
  countriesFetchState: requireFetch(),
  tokenFetchStates: Map<string, FetchState>(),
  ...getAfterLoginPlaceFetchStateState(),
};

const reducer = createReducer<FetchStateType>({}, defaultState);

reducer.on(login, (reducerState) => ({
  ...reducerState,
}));

reducer.on(loginFailed, (reducerState) => ({
  ...reducerState,
}));

reducer.on(loginSuccessful, (reducerState) => ({
  ...reducerState,
  ...getAfterLoginPlaceFetchStateState(),
}));

reducer.on(authenticationRequired, (reducerState) => ({
  ...reducerState,
  authenticationTokenFetchState: requireFetch(),
  ...getAfterLogoutPlaceFetchStateState(),
}));

reducer.on(logout, (reducerState) => ({
  ...reducerState,
  ...getAfterLogoutPlaceFetchStateState(),
}));

reducer.on(fetchLoggedInUser, (reducerState, _) => ({
  ...reducerState,
  loggedInUserFetchState: startFetch(reducerState.loggedInUserFetchState),
}));

reducer.on(loggedInUserFetched, (reducerState, { loggedInUser }) => ({
  ...reducerState,
  loggedInUserFetchState: completeFetch(reducerState.loggedInUserFetchState),
  placeStates: Map(loggedInUser.placePermissions.map(({ placeUuid }) => [placeUuid, requireFetch()])),
}));

reducer.on(passwordChanged, (reducerState) => ({
  ...reducerState,
  loggedInUserFetchState: requireFetch(reducerState.loggedInUserFetchState),
}));

reducer.on(accountReactivated, (reducerState) => ({
  ...reducerState,
  loggedInUserFetchState: requireFetch(reducerState.loggedInUserFetchState),
}));

reducer.on(loggedInUserFetchFailed, (reducerState) => ({
  ...reducerState,
  loggedInUserFetchState: failFetch(reducerState.loggedInUserFetchState),
}));

reducer.on(fetchPlaceLocalization, (reducerState, { placeUuid }) => {
  const { placeLocalizationFetchState } = reducerState;
  const fetchState = startFetch(placeLocalizationFetchState.get(placeUuid));
  return {
    ...reducerState,
    placeLocalizationFetchState: placeLocalizationFetchState.set(placeUuid, fetchState),
  };
});

reducer.on(placeLocalizationFetched, (reducerState, { placeLocalization }) => {
  const { placeLocalizationFetchState } = reducerState;
  const { placeUuid } = placeLocalization;
  const fetchState = completeFetch(placeLocalizationFetchState.get(placeUuid));
  return {
    ...reducerState,
    placeLocalizationFetchState: placeLocalizationFetchState.set(placeUuid, fetchState),
  };
});

reducer.on(placeLocalizationFetchFailed, (reducerState, { placeUuid }) => {
  const { placeLocalizationFetchState } = reducerState;
  const fetchState = failFetch(placeLocalizationFetchState.get(placeUuid));
  return {
    ...reducerState,
    placeLocalizationFetchState: placeLocalizationFetchState.set(placeUuid, fetchState),
  };
});

reducer.on(fetchNotifications, (reducerState) => ({
  ...reducerState,
  notificationFetchState: startFetch(reducerState.notificationFetchState),
}));

reducer.on(notificationsFetched, (reducerState) => {
  return {
    ...reducerState,
    notificationFetchState: completeFetch(reducerState.notificationFetchState),
  };
});

reducer.on(notificationsFetchFailed, (reducerState) => {
  return {
    ...reducerState,
    notificationFetchState: failFetch(reducerState.notificationFetchState),
  };
});

reducer.on(fetchLayouts, (reducerState, { placeUuid }) => {
  const { layoutFetchStates } = reducerState;
  const fetchState = startFetch(layoutFetchStates.get(placeUuid));
  return {
    ...reducerState,
    layoutFetchStates: layoutFetchStates.set(placeUuid, fetchState),
  };
});

reducer.on(layoutsFetched, (reducerState, { placeUuid }) => {
  const { layoutFetchStates } = reducerState;
  const fetchedFetchState = completeFetch(layoutFetchStates.get(placeUuid));
  return {
    ...reducerState,
    layoutFetchStates: layoutFetchStates.set(placeUuid, fetchedFetchState),
  };
});

reducer.on(layoutsFetchFailed, (reducerState, { placeUuid }) => {
  const { layoutFetchStates } = reducerState;
  const fetchState = failFetch(layoutFetchStates.get(placeUuid));
  return { ...reducerState, layoutFetchStates: layoutFetchStates.set(placeUuid, fetchState) };
});

reducer.on(layoutsUpdated, (reducerState, { placeUuid }) => {
  const { layoutFetchStates } = reducerState;
  const fetchState = requireFetch(layoutFetchStates.get(placeUuid));
  return {
    ...reducerState,
    layoutFetchStates: layoutFetchStates.set(placeUuid, fetchState),
  };
});

reducer.on(fetchReservations, (reducerState, { placeUuid }) => {
  const { reservationFetchState } = reducerState;
  const fetchState = startFetch(reservationFetchState.get(placeUuid));
  return {
    ...reducerState,
    reservationFetchState: reservationFetchState.set(placeUuid, fetchState),
  };
});

reducer.on(reservationsFetched, (reducerState, payload) => {
  const { placeUuid } = payload;
  const { reservationFetchState } = reducerState;
  const fetchState = completeFetch(reservationFetchState.get(placeUuid));
  return {
    ...reducerState,
    reservationFetchState: reservationFetchState.set(placeUuid, fetchState),
  };
});

reducer.on(reservationsFetchFailed, (reducerState, payload) => {
  const { placeUuid } = payload;
  const { reservationFetchState } = reducerState;
  const fetchState = failFetch(reservationFetchState.get(placeUuid));
  return {
    ...reducerState,
    reservationFetchState: reservationFetchState.set(placeUuid, fetchState),
  };
});

reducer.on(reservationsUpdated, (reducerState, { placeUuid }) => {
  const { reservationFetchState } = reducerState;
  const fetchState = requireFetch(reservationFetchState.get(placeUuid));
  return { ...reducerState, reservationFetchState: reservationFetchState.set(placeUuid, fetchState) };
});

reducer.on(fetchContact, (reducerState, { placeUuid }) => {
  const { contactFetchState } = reducerState;
  const fetchState = startFetch(contactFetchState.get(placeUuid));
  return { ...reducerState, contactFetchState: contactFetchState.set(placeUuid, fetchState) };
});

reducer.on(contactFetched, (reducerState, { contact }) => {
  const { placeUuid } = contact;
  const { contactFetchState } = reducerState;
  const fetchState = completeFetch(contactFetchState.get(placeUuid));
  return { ...reducerState, contactFetchState: contactFetchState.set(placeUuid, fetchState) };
});

reducer.on(contactFetchFailed, (reducerState, { placeUuid }) => {
  const { contactFetchState } = reducerState;
  const fetchState = failFetch(contactFetchState.get(placeUuid));
  return { ...reducerState, contactFetchState: contactFetchState.set(placeUuid, fetchState) };
});

reducer.on(contactUpdated, (reducerState, { placeUuid }) => {
  const { contactFetchState: fetchStates } = reducerState;
  const fetchState = requireFetch(fetchStates.get(placeUuid));
  return { ...reducerState, contactFetchState: fetchStates.set(placeUuid, fetchState) };
});

reducer.on(fetchHours, (reducerState, { placeUuid }) => {
  const { hoursFetchState } = reducerState;
  const fetchState = startFetch(hoursFetchState.get(placeUuid));
  return { ...reducerState, hoursFetchState: hoursFetchState.set(placeUuid, fetchState) };
});

reducer.on(hoursFetched, (reducerState, { hours }) => {
  const { placeUuid } = hours;
  const { hoursFetchState } = reducerState;
  const fetchState = completeFetch(hoursFetchState.get(placeUuid));
  return {
    ...reducerState,
    hoursFetchState: hoursFetchState.set(placeUuid, fetchState),
  };
});

reducer.on(hoursFetchFailed, (reducerState, { placeUuid }) => {
  const { hoursFetchState } = reducerState;
  const fetchState = failFetch(hoursFetchState.get(placeUuid));
  return { ...reducerState, hoursFetchState: hoursFetchState.set(placeUuid, fetchState) };
});

reducer.on(hoursUpdated, (reducerState, { placeUuid }) => {
  const { hoursFetchState: fetchStates } = reducerState;
  const fetchState = requireFetch(fetchStates.get(placeUuid));
  return { ...reducerState, hoursFetchState: fetchStates.set(placeUuid, fetchState) };
});

reducer.on(fetchProfile, (reducerState, { placeUuid }) => {
  const { profileFetchState } = reducerState;
  const fetchState = startFetch(profileFetchState.get(placeUuid));
  return { ...reducerState, profileFetchState: profileFetchState.set(placeUuid, fetchState) };
});

reducer.on(profileFetched, (reducerState, { profile }) => {
  const { profileFetchState } = reducerState;
  const { placeUuid } = profile;
  const fetchState = completeFetch(profileFetchState.get(placeUuid));
  return { ...reducerState, profileFetchState: profileFetchState.set(placeUuid, fetchState) };
});

reducer.on(profileFetchFailed, (reducerState, { placeUuid }) => {
  const { profileFetchState } = reducerState;
  const fetchState = failFetch(profileFetchState.get(placeUuid));
  return { ...reducerState, profileFetchState: profileFetchState.set(placeUuid, fetchState) };
});

reducer.on(profileUpdated, (reducerState, { placeUuid }) => {
  const { profileFetchState } = reducerState;
  const fetchState = requireFetch(profileFetchState.get(placeUuid));
  return { ...reducerState, profileFetchState: profileFetchState.set(placeUuid, fetchState) };
});

reducer.on(fetchReservationSetting, (reducerState, { placeUuid }) => {
  const { reservationSettingFetchState } = reducerState;
  const fetchState = startFetch(reservationSettingFetchState.get(placeUuid));
  return {
    ...reducerState,
    reservationSettingFetchState: reservationSettingFetchState.set(placeUuid, fetchState),
  };
});

reducer.on(reservationSettingFetched, (reducerState, { reservationSetting }) => {
  const { placeUuid } = reservationSetting;
  const { reservationSettingFetchState } = reducerState;
  const fetchState = completeFetch(reservationSettingFetchState.get(placeUuid));
  return {
    ...reducerState,
    reservationSettingFetchState: reservationSettingFetchState.set(placeUuid, fetchState),
  };
});

reducer.on(reservationSettingFetchFailed, (reducerState, { placeUuid }) => {
  const { reservationSettingFetchState } = reducerState;
  const fetchState = failFetch(reservationSettingFetchState.get(placeUuid));
  return {
    ...reducerState,
    reservationSettingFetchState: reservationSettingFetchState.set(placeUuid, fetchState),
  };
});

reducer.on(reservationSettingUpdated, (reducerState, { placeUuid }) => {
  const { reservationSettingFetchState: fetchStates } = reducerState;
  const fetchState = requireFetch(fetchStates.get(placeUuid));
  return { ...reducerState, reservationSettingFetchState: fetchStates.set(placeUuid, fetchState) };
});

reducer.on(facebookPageUpdated, (reducerState, { placeUuid }) => {
  const { facebookPage } = reducerState;
  const fetchState = requireFetch(facebookPage.get(placeUuid));
  return {
    ...reducerState,
    facebookPage: facebookPage.set(placeUuid, fetchState),
  };
});

reducer.on(fetchPlaceLayouts, (reducerState, { placeUuid }) => {
  const fetchState = startFetch(reducerState.placeLayouts.get(placeUuid));
  return {
    ...reducerState,
    placeLayouts: reducerState.placeLayouts.set(placeUuid, fetchState),
  };
});

reducer.on(placeLayoutsFetched, (reducerState, { placeLayouts }) => {
  const fetchState = completeFetch(reducerState.placeLayouts.get(placeLayouts.placeUuid));
  return {
    ...reducerState,
    placeLayouts: reducerState.placeLayouts.set(placeLayouts.placeUuid, fetchState),
  };
});

reducer.on(placeAdded, (reducerState, { placeName }) => {
  const fetchState = requireFetch();
  return {
    ...reducerState,
    placeLayouts: reducerState.placeLayouts.set(placeName.uuid, fetchState),
  };
});

reducer.on(placeLayoutsFetchFailed, (reducerState, { placeUuid }) => {
  const { placeLayouts } = reducerState;
  return { ...reducerState, placeLayouts: placeLayouts.set(placeUuid, failFetch(placeLayouts.get(placeUuid))) };
});

reducer.on(fetchCompanyGuests, (reducerState) => {
  const { companyGuests } = reducerState;
  return {
    ...reducerState,
    companyGuests: startFetch(companyGuests),
  };
});

reducer.on(companyGuestsFetched, (reducerState) => {
  const { companyGuests } = reducerState;
  return {
    ...reducerState,
    companyGuests: completeFetch(companyGuests),
  };
});

reducer.on(companyGuestsFetchFailed, (reducerState) => {
  const { companyGuests } = reducerState;
  return { ...reducerState, companyGuests: failFetch(companyGuests) };
});

reducer.on(fetchPlace, (reducerState, { placeUuid }) => {
  const fetchState = startFetch(reducerState.places.get(placeUuid));
  return {
    ...reducerState,
    places: reducerState.places.set(placeUuid, fetchState),
  };
});

reducer.on(placeFetched, (reducerState, { place }) => {
  const fetchState = completeFetch(reducerState.places.get(place.uuid));
  return {
    ...reducerState,
    places: reducerState.places.set(place.uuid, fetchState),
  };
});

reducer.on(placeFetchFailed, (reducerState, { placeUuid }) => {
  const { places } = reducerState;
  return { ...reducerState, places: places.set(placeUuid, failFetch(places.get(placeUuid))) };
});

reducer.on(fetchFacebookPages, (reducerState, { placeUuid }) => {
  const { facebookPages } = reducerState;
  return { ...reducerState, facebookPages: facebookPages.set(placeUuid, startFetch(facebookPages.get(placeUuid))) };
});
reducer.on(facebookPagesFetched, (reducerState, { placeUuid }) => {
  const { facebookPages } = reducerState;
  return { ...reducerState, facebookPages: facebookPages.set(placeUuid, completeFetch(facebookPages.get(placeUuid))) };
});
reducer.on(facebookPagesFetchFailed, (reducerState, { placeUuid }) => {
  const { facebookPages } = reducerState;
  return { ...reducerState, facebookPages: facebookPages.set(placeUuid, failFetch(facebookPages.get(placeUuid))) };
});

reducer.on(fetchFacebookPage, (reducerState, { placeUuid }) => {
  const { facebookPage } = reducerState;
  return { ...reducerState, facebookPage: facebookPage.set(placeUuid, startFetch(facebookPage.get(placeUuid))) };
});
reducer.on(facebookPageFetched, (reducerState, { placeUuid }) => {
  const { facebookPage } = reducerState;
  return { ...reducerState, facebookPage: facebookPage.set(placeUuid, completeFetch(facebookPage.get(placeUuid))) };
});
reducer.on(facebookPageFetchFailed, (reducerState, { placeUuid }) => {
  const { facebookPage } = reducerState;
  return { ...reducerState, facebookPage: facebookPage.set(placeUuid, failFetch(facebookPage.get(placeUuid))) };
});

reducer.on(fetchActivatePlace, ({ activatePlaceFetchState, ...others }) => {
  return { ...others, activatePlaceFetchState: startFetch(activatePlaceFetchState) };
});

reducer.on(activatePlaceFetched, ({ activatePlaceFetchState, ...others }) => {
  return { ...others, activatePlaceFetchState: completeFetch(activatePlaceFetchState) };
});

reducer.on(activatePlaceFetchFailed, ({ activatePlaceFetchState, ...others }) => {
  return { ...others, activatePlaceFetchState: failFetch(activatePlaceFetchState) };
});

reducer.on(fetchCountries, (reducerState) => {
  const { countriesFetchState } = reducerState;
  const fetchState = startFetch(countriesFetchState);
  return { ...reducerState, countriesFetchState: fetchState };
});

reducer.on(countriesFetched, (reducerState) => {
  const { countriesFetchState } = reducerState;
  const fetchState = completeFetch(countriesFetchState);
  return { ...reducerState, countriesFetchState: fetchState };
});

reducer.on(countriesFetchFailed, (reducerState) => {
  const { countriesFetchState } = reducerState;
  const fetchState = failFetch(countriesFetchState);
  return { ...reducerState, countriesFetchState: fetchState };
});

reducer.on(fetchEmployees, (reducerState) => {
  const { employeeFetchState } = reducerState;
  const fetchState = startFetch(employeeFetchState);
  return { ...reducerState, employeeFetchState: fetchState };
});

reducer.on(employeesFetched, (reducerState) => {
  const { employeeFetchState } = reducerState;
  const fetchState = completeFetch(employeeFetchState);
  return { ...reducerState, employeeFetchState: fetchState };
});

reducer.on(employeesFetchFailed, (reducerState) => {
  const { employeeFetchState } = reducerState;
  const fetchState = failFetch(employeeFetchState);
  return { ...reducerState, employeeFetchState: fetchState };
});

reducer.on(fetchPrices, (reducerState, { placeUuid, feature }) => {
  const { prices } = reducerState;
  const key = getPriceKey(placeUuid, feature);
  return { ...reducerState, prices: prices.set(key, startFetch(prices.get(key))) };
});

reducer.on(pricesFetched, (reducerState, { placeUuid, feature }) => {
  const { prices } = reducerState;
  const key = getPriceKey(placeUuid, feature);
  return { ...reducerState, prices: prices.set(key, completeFetch(prices.get(key))) };
});

reducer.on(pricesFetchFailed, (reducerState, { placeUuid, feature }) => {
  const { prices } = reducerState;
  const key = getPriceKey(placeUuid, feature);
  return { ...reducerState, prices: prices.set(key, failFetch(prices.get(key))) };
});

reducer.on(fetchSubscriptions, (reducerState, { feature, placeUuid }) => {
  const { subscriptions } = reducerState;
  const key = getSubscriptionKey(placeUuid, feature);
  return {
    ...reducerState,
    subscriptions: subscriptions.set(key, startFetch(subscriptions.get(key))),
  };
});

reducer.on(subscriptionsFetched, (reducerState, { feature, placeUuid }) => {
  const { subscriptions } = reducerState;
  const key = getSubscriptionKey(placeUuid, feature);
  return {
    ...reducerState,
    subscriptions: subscriptions.set(key, completeFetch(subscriptions.get(key))),
  };
});

reducer.on(subscriptionCreated, (reducerState, { placeUuid, feature }) => {
  const { subscriptions } = reducerState;
  const key = getSubscriptionKey(placeUuid, feature);
  const fetchState = requireFetch(subscriptions.get(key));
  return { ...reducerState, subscriptions: subscriptions.set(key, fetchState) };
});

reducer.on(subscriptionsFetchFailed, (reducerState, { feature, placeUuid }) => {
  const { subscriptions } = reducerState;
  const key = getSubscriptionKey(placeUuid, feature);
  return {
    ...reducerState,
    subscriptions: subscriptions.set(key, failFetch(subscriptions.get(key))),
  };
});

function fetchToken(reducerState: FetchStateType, token: string): FetchStateType {
  const { tokenFetchStates } = reducerState;
  return {
    ...reducerState,
    tokenFetchStates: tokenFetchStates.set(token, startFetch(requireFetch(tokenFetchStates.get(token)))),
  };
}

function tokenFetched(reducerState: FetchStateType, token: string): FetchStateType {
  const { tokenFetchStates } = reducerState;
  return {
    ...reducerState,
    tokenFetchStates: tokenFetchStates.set(token, completeFetch(tokenFetchStates.get(token))),
  };
}

function tokenFetchFailed(reducerState: FetchStateType, token: string): FetchStateType {
  const { tokenFetchStates } = reducerState;
  return {
    ...reducerState,
    tokenFetchStates: tokenFetchStates.set(token, failFetch(tokenFetchStates.get(token))),
  };
}

function registerActions<T, InitialPayload extends T = T, SuccessPayload extends T = T, FailPayload extends T = T>(
  initialAction: SimpleActionCreator<InitialPayload, object>,
  successAction: SimpleActionCreator<SuccessPayload, object>,
  failAction: SimpleActionCreator<FailPayload, object>,
  getUniqueIdentifier: (payload: T) => string
) {
  reducer.on(initialAction, (reducerState, payload) => {
    return fetchToken(reducerState, getUniqueIdentifier(payload));
  });
  reducer.on(successAction, (reducerState, payload) => {
    return tokenFetched(reducerState, getUniqueIdentifier(payload));
  });
  reducer.on(failAction, (reducerState, payload) => {
    return tokenFetchFailed(reducerState, getUniqueIdentifier(payload));
  });
}

registerActions<{ token: string }>(fetchEmployeeInvitation, employeeInvitationFetched, employeeInvitationFetchFailed, (payload) => payload.token);

export default reducer;
